import React, { useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';

import api, { apiBlob } from '../../../api/api';
import NameForm from "./Forms/NameForm";

const imageExtensions = ["gif", "jpg", "jpeg", "jepg", "png", "webp", "svg", "ico", "bmp"];
const videoExtensions = ["mp4", "webm", "ogg"];
const audioExtensions = ["mp3", "wav", "ogg", "m4a", "aac"];
const docExtensions = ["pdf", "html", "htm"];
const textExtensions = ["txt", "log", "json", "js", "css", "html", "xml", "md", "sql", "csv"];

const extIconClass = (ext) => {
    const e = (ext || '').toLowerCase();
    if (videoExtensions.includes(e)) return "fad fa-file-video text-primary";
    if (audioExtensions.includes(e)) return "fad fa-file-audio text-info";
    if (e === "pdf") return "fad fa-file-pdf text-danger";
    if (["zip", "rar", "7z", "tar", "gz"].includes(e)) return "fad fa-file-archive text-warning";
    if (["doc", "docx", "odt"].includes(e)) return "fad fa-file-word text-primary";
    if (["xls", "xlsx", "csv", "ods"].includes(e)) return "fad fa-file-excel text-success";
    if (textExtensions.includes(e)) return "fad fa-file-code text-secondary";
    return "fad fa-file text-muted";
};

const ListItem = ({ item, Sync, path, openFolder, remove, viewType }) => {
    const { t } = useTranslation('common');
    const [rename, setRename] = useState(false);
    const [show, setShow] = useState(false);
    const [textContent, setTextContent] = useState("");
    const [loadingText, setLoadingText] = useState(false);
    const [textError, setTextError] = useState(false);
    const toastIdRef = useRef(null);

    if (!item || !item.attributes) return null;

    const ext = (item.attributes.extension || '').toLowerCase();

    const handleClose = () => {
        setRename(false);
        setShow(false);
        setTextContent("");
        setTextError(false);
    };

    const handleOpenRename = () => {
        setRename(true);
    };

    const handleOpenPreview = () => {
        setShow(true);
        if (textExtensions.includes(ext)) {
            fetchTextContent();
        }
    };

    const handleOpenFolder = () => {
        if (item.type === "folder") {
            openFolder(item.id);
        }
    };

    const fetchTextContent = () => {
        const url = process.env.REACT_APP_SITE_URL + item.attributes.path;
        setLoadingText(true);
        setTextContent("");
        setTextError(false);

        fetch(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }
                return response.text();
            })
            .then(text => {
                setTextContent(text);
                setLoadingText(false);
            })
            .catch(err => {
                console.error("fetchTextContent error:", err);
                setTextError(true);
                setLoadingText(false);
            });
    };

    const isViewable = (ext) => {
        const e = (ext || '').toLowerCase();
        return imageExtensions.includes(e) ||
            videoExtensions.includes(e) ||
            audioExtensions.includes(e) ||
            docExtensions.includes(e) ||
            textExtensions.includes(e);
    };

    const handleGetLink = () => {
        navigator.clipboard.writeText(process.env.REACT_APP_SITE_URL + item.attributes.path);

        toastIdRef.current = toast(t('Processing'), {
            position: "bottom-left",
            autoClose: false
        });

        toast.update(toastIdRef.current, {
            render: () => <div> <i className="fa-spin fas fa-spinner"></i> {t('Processing')} </div>,
            autoClose: false,
            closeOnClick: false,
        });

        toast.update(toastIdRef.current, {
            render: () => (
                <div>
                    <div className="h5"> <i className="icon fal fa-copy"></i> {t('Pages.FileManager.get_link_success_toast.Title')}</div>
                    <div className="h6">{t('Pages.FileManager.get_link_success_toast.Body')}</div>
                </div>
            ),
            type: toast.TYPE.SUCCESS,
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
        });
    };

    const handleDownload = () => {
        toastIdRef.current = toast(t('Processing'), {
            position: "bottom-left",
            autoClose: false
        });
        toast.update(toastIdRef.current, {
            render: () => <div> <i className="fa-spin fas fa-spinner"></i> {t('Processing')} </div>,
            autoClose: false,
            closeOnClick: false,
        });

        apiBlob.post(`/FileManager/?path=${item.attributes.path}`).then(response => {
            var a = document.createElement("a");
            document.body.appendChild(a);
            a.style.display = "none";
            const blob = new Blob([response.data]),
                url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download = item.attributes.name + (item.attributes.extension ? '.' + item.attributes.extension : '');
            a.click();
            window.URL.revokeObjectURL(url);
            toast.dismiss(toastIdRef.current);
        }).catch(error => {
            console.error("Download error:", error);
            toast.update(toastIdRef.current, {
                render: () => (
                    <div>
                        <div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div>
                        <div className="h6">{t('Error.NoConnection.Body')}</div>
                    </div>
                ),
                type: toast.TYPE.ERROR,
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });
        });
    };

    const onSubmitRename = (postData, toastId) => {
        api.put(`/FileManager/?path=${path}`, postData).then(response => {
            Sync(path);

            toast.update(toastId, {
                render: () => (
                    <div>
                        <div className="h5">
                            <i className="icon fal fa-exchange-alt"></i>
                            {t('Pages.FileManager.rename_success_toast.Title')}
                        </div>
                        <div className="h6">
                            {t('Pages.FileManager.rename_success_toast.Body')}
                        </div>
                    </div>
                ),
                type: toast.TYPE.SUCCESS,
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });

            setRename(false);
        }).catch(error => {
            console.error("Rename error:", error);
            toast.update(toastId, {
                render: () => (
                    <div>
                        <div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div>
                        <div className="h6">{t('Error.NoConnection.Body')}</div>
                    </div>
                ),
                type: toast.TYPE.ERROR,
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });
        });
    };

    const renderIcon = () => {
        const url = process.env.REACT_APP_SITE_URL + item.attributes.path;

        if (item.type === "folder") {
            return (
                <div className="file-icon-wrapper cursor-pointer" onClick={handleOpenFolder}>
                    <i className="fad fa-folder text-warning fa-3x"></i>
                </div>
            );
        }

        if (imageExtensions.includes(ext)) {
            return (
                <LazyLoadImage
                    src={url}
                    alt={item.id}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/assets/css/img/gallery1.png";
                    }}
                />
            );
        }

        return <div className="file-icon-wrapper"><i className={extIconClass(ext) + " fa-3x"}></i></div>;
    };

    const renderThumb = () => {
        return (
            <div className="product-thumb">
                {renderIcon()}

                <div className="product-action-link">
                    {item.type === "folder" ? (
                        <div className="my-1 cursor-pointer" onClick={handleOpenFolder} title={t("Pages.FileManager.action.open")}>
                            <i className="far fa-folder-open"></i>
                        </div>
                    ) : (
                        <>
                            <div className="my-1 cursor-pointer" onClick={handleDownload} title={t("Pages.FileManager.action.download")}>
                                <i className="far fa-download"></i>
                            </div>
                            <div className="my-1 cursor-pointer" onClick={handleGetLink} title={t("Pages.FileManager.action.link")}>
                                <i className="far fa-link"></i>
                            </div>
                            {isViewable(ext) && (
                                <div className="my-1 cursor-pointer" onClick={handleOpenPreview} title={t("Pages.FileManager.action.view")}>
                                    <i className="far fa-eye"></i>
                                </div>
                            )}
                        </>
                    )}
                    <div className="my-1 cursor-pointer" onClick={handleOpenRename} title={t("Pages.FileManager.action.rename")}>
                        <i className="far fa-exchange-alt"></i>
                    </div>
                    <div className="my-1 cursor-pointer text-danger" onClick={() => remove(item)} title={t("Pages.FileManager.action.remove")}>
                        <i className="far fa-trash"></i>
                    </div>
                </div>
            </div>
        );
    };

    const renderPreviewContent = () => {
        const url = process.env.REACT_APP_SITE_URL + item.attributes.path;

        if (imageExtensions.includes(ext)) {
            return (
                <div className="text-center p-3">
                    <img src={url} alt={item.attributes.name} className="img-fluid rounded shadow-sm border" style={{ maxHeight: '75vh', objectFit: 'contain' }} />
                </div>
            );
        }

        if (videoExtensions.includes(ext)) {
            return (
                <div className="text-center p-2">
                    <video className="w-100 rounded shadow-sm border" style={{ maxHeight: '70vh' }} controls autoPlay>
                        <source src={url} />
                        Your browser does not support the video tag.
                    </video>
                </div>
            );
        }

        if (audioExtensions.includes(ext)) {
            return (
                <div className="text-center p-5 bg-light rounded shadow-sm border">
                    <i className="fad fa-music-alt fa-4x text-info mb-4 d-block"></i>
                    <audio className="w-100" controls autoPlay>
                        <source src={url} />
                        Your browser does not support the audio element.
                    </audio>
                </div>
            );
        }

        if (docExtensions.includes(ext)) {
            return (
                <div className="w-100">
                    <iframe className="w-100 rounded border shadow-sm" height="650px" title={item.attributes.name} src={url}></iframe>
                </div>
            );
        }

        if (textExtensions.includes(ext)) {
            if (loadingText) {
                return (
                    <div className="text-center py-5">
                        <i className="fad fa-spinner fa-spin fa-3x text-primary mb-3"></i>
                        <h5>Loading text content...</h5>
                    </div>
                );
            }
            if (textError) {
                return (
                    <div className="alert alert-danger text-center m-3">
                        <i className="fad fa-exclamation-triangle mr-2"></i>
                        Failed to load text preview. You can download the file to view it.
                    </div>
                );
            }
            return (
                <div className="p-2">
                    <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                        <span className="badge badge-secondary">{ext.toUpperCase()} File</span>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => {
                            navigator.clipboard.writeText(textContent);
                            toast.success("تم النسخ إلى الحافظة!");
                        }}>
                            <i className="far fa-copy mr-1"></i> Copy Content
                        </button>
                    </div>
                    <pre className="p-3 text-left bg-dark text-light border rounded" style={{
                        maxHeight: '500px',
                        overflowY: 'auto',
                        direction: 'ltr',
                        fontFamily: 'Consolas, monospace',
                        fontSize: '13.5px',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all'
                    }}>
                        {textContent}
                    </pre>
                </div>
            );
        }

        return (
            <div className="text-center py-5 bg-light rounded border">
                <i className="fad fa-file-alt fa-4x text-secondary mb-3"></i>
                <h5>No preview available for this file type.</h5>
                <p className="text-muted">You can download the file to view it locally.</p>
            </div>
        );
    };

    return (
        <>
            {rename && (
                <Modal
                    className="file-manager-page"
                    size="md"
                    show={rename}
                    onHide={handleClose}
                    backdrop="static"
                    keyboard={false}
                    enforceFocus={false} >
                    <Modal.Header closeButton>
                        <Modal.Title> <i className="icon fal fa-exchange-alt"></i> {t('Pages.FileManager.action.rename')} </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="widget widget-category">
                            <div className="widget-body">
                                <NameForm
                                    id={item.id}
                                    Title={item.attributes.name}
                                    path={path}
                                    onSubmit={onSubmitRename}
                                />
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            )}

            {item.type !== "folder" && show && (
                <Modal
                    className="file-manager-page"
                    size="xl"
                    show={show}
                    onHide={handleClose}
                    backdrop="static"
                    keyboard={false}
                    enforceFocus={false} >
                    <Modal.Header closeButton>
                        <Modal.Title> <i className="icon fal fa-eye"></i> {item.attributes.name} </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ minHeight: '300px' }}>
                        {renderPreviewContent()}
                    </Modal.Body>
                </Modal>
            )}

            <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-4">
                {/* Grid view card */}
                <div className="shop-item card h-100 shadow-sm border border-light rounded"
                    onDoubleClick={item.type === "folder" ? handleOpenFolder : undefined}>
                    {renderThumb()}
                    <div className="product-content p-3 text-center border-top">
                        <h6 className="title text-dark text-truncate font-weight-semibold cursor-pointer mb-0"
                            style={{ fontSize: '14px' }}
                            onClick={() => {
                                if (item.type === "folder") {
                                    handleOpenFolder();
                                } else if (isViewable(ext)) {
                                    handleOpenPreview();
                                }
                            }}>
                            {item.attributes.name}
                        </h6>
                        {item.attributes.size ? (
                            <small className="text-muted d-block mt-1">
                                {Math.round(item.attributes.size / 1024, 2)} KB
                            </small>
                        ) : null}
                    </div>
                </div>

                {/* List view card */}
                <div className="product-list-item card flex-row align-items-center p-2 mb-2 shadow-sm border border-light"
                    style={{ direction: 'rtl', minHeight: '60px' }}
                    onDoubleClick={item.type === "folder" ? handleOpenFolder : undefined}>

                    {/* 1. Icon */}
                    <div className="m-2" style={{ width: '45px', flexShrink: 0 }} onClick={item.type === "folder" ? handleOpenFolder : undefined}>
                        {item.type === "folder" ? (
                            <i className="fad fa-folder text-warning fa-2.5x cursor-pointer"></i>
                        ) : imageExtensions.includes(ext) ? (
                            <div className="cursor-pointer" style={{ width: '40px', height: '40px', overflow: 'hidden', borderRadius: '4px', border: '1px solid #dee2e6' }} onClick={handleOpenPreview}>
                                <img src={process.env.REACT_APP_SITE_URL + item.attributes.path} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                        ) : (
                            <i className={`${extIconClass(ext)} fa-2x ${isViewable(ext) ? 'cursor-pointer' : ''}`} onClick={isViewable(ext) ? handleOpenPreview : undefined}></i>
                        )}
                    </div>

                    {/* 2. File Name */}
                    <div className="flex-grow-1 text-right px-2" style={{ overflow: 'hidden', minWidth: '150px' }}>
                        <h6 className="title text-dark text-truncate font-weight-bold mb-0 cursor-pointer"
                            onClick={() => {
                                if (item.type === "folder") {
                                    handleOpenFolder();
                                } else if (isViewable(ext)) {
                                    handleOpenPreview();
                                }
                            }}>
                            {item.attributes.name}
                        </h6>
                    </div>

                    {/* 3. File Size */}
                    <div className="text-center px-3 text-muted d-none d-md-block" style={{ width: '100px', fontSize: '13px' }}>
                        {item.type === "folder" ? "-" : `${Math.round(item.attributes.size / 1024, 1)} KB`}
                    </div>

                    {/* 4. Modified Date */}
                    <div className="text-center px-3 text-muted d-none d-lg-block" style={{ width: '150px', fontSize: '13px' }}>
                        {item.attributes.modified || item.attributes.created}
                    </div>

                    {/* 5. Actions */}
                    <div className="d-flex align-items-center mr-auto pl-2">
                        {item.type === "folder" ? (
                            <button className="btn btn-sm btn-light p-1 mx-1 text-primary" onClick={handleOpenFolder} title={t("Pages.FileManager.action.open")}>
                                <i className="far fa-folder-open fa-lg"></i>
                            </button>
                        ) : (
                            <>
                                <button className="btn btn-sm btn-light p-1 mx-1 text-success" onClick={handleDownload} title={t("Pages.FileManager.action.download")}>
                                    <i className="far fa-download fa-lg"></i>
                                </button>
                                <button className="btn btn-sm btn-light p-1 mx-1 text-info" onClick={handleGetLink} title={t("Pages.FileManager.action.link")}>
                                    <i className="far fa-link fa-lg"></i>
                                </button>
                                {isViewable(ext) && (
                                    <button className="btn btn-sm btn-light p-1 mx-1 text-dark" onClick={handleOpenPreview} title={t("Pages.FileManager.action.view")}>
                                        <i className="far fa-eye fa-lg"></i>
                                    </button>
                                )}
                            </>
                        )}
                        <button className="btn btn-sm btn-light p-1 mx-1 text-warning" onClick={handleOpenRename} title={t("Pages.FileManager.action.rename")}>
                            <i className="far fa-exchange-alt fa-lg"></i>
                        </button>
                        <button className="btn btn-sm btn-light p-1 mx-1 text-danger" onClick={() => remove(item)} title={t("Pages.FileManager.action.remove")}>
                            <i className="far fa-trash fa-lg"></i>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ListItem;